import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from "react-hook-form";
import { z } from 'zod';
import InputFloating from "@/components/atoms/InputFloating";
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useUserData } from "@/lib/useQuery/user/useUserData";
// import useResetPassword from '@/lib/useQuery/user/useResetPassword';
import { useSetupAccount } from '@/lib/useQuery/user/useAccountSetup';

type Props = {
    show: boolean,
    onHide: () => void,
}

export default function UpdatePasswordModal({
    show,
    onHide,
}: Props) {
    const { data: userData } = useUserData();

    const schema = z.object({
        password: z.string().min(6, "Password Must be at least 6 characters long").max(30, "Password Must not be longer than 30 characters"),
        confirm: z.string().min(6, "Password Must be at least 6 characters long").max(30, "Password Must not be longer than 30 characters"),
    })
    .refine(data => data.password === data.confirm, {
        message: "Passwords do not match",
        path: ["confirm"],
    });
    type Inputs = z.infer<typeof schema>;
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<Inputs>({
        resolver: zodResolver(schema),
    })
    
    const onCloseQuestionModal = () => {
        reset({
            password: "",
            confirm: "",
        });
        onHide();
    }

    const { isPending, mutateAsync: mutateSetupAccount } = useSetupAccount();

    const onSubmit = async ({password}: {
        password: string
    }) => {
        const userId = userData?.id;
        const userFirstName = userData?.first_name;
        const userLastName = userData?.last_name;
        if(!userId || !userFirstName || !userLastName) return;
  
        try {
            const result = await mutateSetupAccount({
                first_name: userFirstName,
                last_name: userLastName,
                password,
            });
            if(!result.error) {
                SuccessPopup("Password changed successfully");
                onCloseQuestionModal();
            } else {
                const error = result.error.message || "An error occurred. Please try again later.";
                ErrorPopup(error);
            }
        } catch (err) {
            ErrorPopup("An error occurred. Please try again later.");
        }
    }

    const onSubmitHandler = () => {
        handleSubmit(onSubmit)();
    }

    return (
        <>
            {isPending && (<LoaderWithBackdrop />)}

            <Dialog open={show} onOpenChange={onCloseQuestionModal} defaultOpen={false} >
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle className='ps-3'>
                            <div className="p-0 m-0 text-start">
                                Change Password
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] px-3">
                        <hr className="mb-3" />

                        <div className="px-1 space-y-4">
                            <InputFloating
                                showError
                                type="password"
                                label="New Password"
                                register={register("password", { required: true })}
                                errorMessage={errors.password?.message}
                            />
                            <InputFloating
                                showError
                                type="password"
                                label="Confirm New Password"
                                register={register("confirm", { required: true })}
                                errorMessage={errors.confirm?.message}
                            />
                        </div>
                    </ScrollArea>

                    <DialogFooter className="px-3">
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant="secondary" type="button" onClick={onCloseQuestionModal}>Back</Button>
                            <Button size="sm" type="button" onClick={onSubmitHandler}>Next</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
